import CryptoJS from "crypto-js";
import i18next from "i18next";
import { useSyncExternalStore } from "react";
import type { PollConfig } from "../models/poll.types";

// Zero-width Unicode prefix to mark encrypted content
// U+200B (zero-width space) + U+200C (zero-width non-joiner) + U+200D (zero-width joiner) + U+FEFF (BOM)
export const ENCRYPTED_PREFIX = "​‌‍﻿";

// Reversed prefix embedded inside plaintext before encryption — used to verify correct key on decrypt
export const ENCRYPTED_SUFFIX = "﻿‍‌​";

export function encryptedPlaceholder() {
  return i18next.t("encrypted");
}

// --- Core encrypt/decrypt ---

export function isEncrypted(text: string): boolean {
  return text.startsWith(ENCRYPTED_PREFIX);
}

export function encryptField(text: string, passphrase: string): string {
  const ciphertext = CryptoJS.AES.encrypt(text + ENCRYPTED_SUFFIX, passphrase).toString();
  return ENCRYPTED_PREFIX + ciphertext;
}

/**
 * Attempt decryption with each passphrase in turn. Returns:
 * - the original text if it isn't encrypted
 * - the decrypted text if any passphrase succeeds
 * - null if the text is encrypted and no passphrase decrypts it
 */
export function tryDecrypt(text: string, passphrases: string[]): string | null {
  if (!isEncrypted(text)) return text;
  const ciphertext = text.slice(ENCRYPTED_PREFIX.length);
  for (const passphrase of passphrases) {
    try {
      const decrypted = CryptoJS.AES.decrypt(ciphertext, passphrase).toString(CryptoJS.enc.Utf8);
      if (decrypted && decrypted.endsWith(ENCRYPTED_SUFFIX)) {
        return decrypted.slice(0, -ENCRYPTED_SUFFIX.length);
      }
    } catch {
      // try next passphrase
    }
  }
  return null;
}

export function decryptField(text: string, passphrases: string[]): string {
  return tryDecrypt(text, passphrases) ?? encryptedPlaceholder();
}

/**
 * Decrypt a field, returning the placeholder if encrypted but no passphrase decrypts it.
 * Marks the session as having unresolvable encrypted content (for toast notifications)
 * whenever a placeholder is returned.
 */
export function decryptFieldOrPlaceholder(text: string, passphrases: string[], sessionUid?: string): string {
  const r = tryDecrypt(text, passphrases);
  if (r !== null) return r;
  if (sessionUid) markEncryptedContentDetected(sessionUid);
  return encryptedPlaceholder();
}

// --- PollConfig helpers ---

export function encryptPollConfig(config: PollConfig, passphrase: string): PollConfig {
  if (config.type === "discrete") {
    return {
      ...config,
      options: config.options.map(opt => ({ uid: opt.uid, label: encryptField(opt.label, passphrase) })),
    };
  }
  if (config.type === "slider") {
    return {
      ...config,
      ...(config.minLabel != null && { minLabel: encryptField(config.minLabel, passphrase) }),
      ...(config.maxLabel != null && { maxLabel: encryptField(config.maxLabel, passphrase) }),
    };
  }
  return config;
}

/**
 * Like decryptPollConfig, but reports whether any encrypted label failed to decrypt
 * with the supplied passphrases. Does NOT call markEncryptedContentDetected — callers
 * are expected to react to the `failed` flag.
 */
export function tryDecryptPollConfig(config: PollConfig, passphrases: string[]): { config: PollConfig; failed: boolean } {
  let failed = false;
  const attempt = (text: string): string => {
    const r = tryDecrypt(text, passphrases);
    if (r === null) {
      failed = true;
      return encryptedPlaceholder();
    }
    return r;
  };
  if (config.type === "discrete") {
    return {
      config: {
        ...config,
        options: config.options.map(opt => ({ uid: opt.uid, label: attempt(opt.label) })),
      },
      failed,
    };
  }
  if (config.type === "slider") {
    return {
      config: {
        ...config,
        ...(config.minLabel != null && { minLabel: attempt(config.minLabel) }),
        ...(config.maxLabel != null && { maxLabel: attempt(config.maxLabel) }),
      },
      failed,
    };
  }
  return { config, failed };
}

export function decryptPollConfig(config: PollConfig, passphrases: string[], sessionUid?: string): PollConfig {
  const { config: decrypted, failed } = tryDecryptPollConfig(config, passphrases);
  if (failed && sessionUid) markEncryptedContentDetected(sessionUid);
  return decrypted;
}

// --- Passphrase store (in-memory only, scoped by sessionUid) ---

const passphrases = new Map<string, string[]>();
const encryptedDetected = new Set<string>();
const listeners = new Set<() => void>();

function emitChange() {
  listeners.forEach(l => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function setSessionPassphrases(sessionUid: string, next: string[]): void {
  const filtered = next.filter(p => p.length > 0);
  if (filtered.length > 0) {
    passphrases.set(sessionUid, filtered);
    // Clear the "encrypted detected" flag — re-render will re-emit if any cards still don't decrypt
    encryptedDetected.delete(sessionUid);
  } else {
    passphrases.delete(sessionUid);
  }
  emitChange();
}

export function getSessionPassphrases(sessionUid: string): string[] {
  return passphrases.get(sessionUid) ?? [];
}

export function getPrimarySessionPassphrase(sessionUid: string): string | null {
  return passphrases.get(sessionUid)?.[0] ?? null;
}

// Cache identity-stable empty array reference so React's useSyncExternalStore
// doesn't see a new array on every read when no passphrases are set.
const EMPTY: string[] = [];

export function useSessionPassphrases(sessionUid: string): string[] {
  return useSyncExternalStore(subscribe, () => passphrases.get(sessionUid) ?? EMPTY);
}

// --- Encrypted content detection (for toast notifications) ---

export function markEncryptedContentDetected(sessionUid: string): void {
  if (encryptedDetected.has(sessionUid)) return;
  encryptedDetected.add(sessionUid);
  // Defer to avoid triggering state updates during render (called from useMemo)
  queueMicrotask(emitChange);
}

export function useEncryptedContentDetected(sessionUid: string): boolean {
  return useSyncExternalStore(subscribe, () => encryptedDetected.has(sessionUid));
}
